/**
 * TypeScript interfaces for Marketing Materials Management System
 */

export interface MaterialCategory {
  id: string;
  name: string;
  description: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface MarketingMaterial {
  id: string;
  title: string;
  description: string | null;
  file_url: string;
  file_name: string;
  file_type: string;
  file_size: number;
  category_id: string | null;
  uploaded_by: number;
  is_published: boolean;
  download_count: number;
  created_at: string;
  updated_at: string;
  // Joined data
  category?: MaterialCategory;
  uploader?: {
    id: number;
    full_name: string;
    email: string;
  };
}

export interface CreateMaterialInput {
  title: string;
  description?: string;
  file: File;
  category_id: string;
}

export interface UpdateMaterialInput {
  id: string;
  title?: string;
  description?: string;
  category_id?: string;
  is_published?: boolean;
}

export interface CreateCategoryInput {
  name: string;
  description?: string;
  display_order?: number;
}

export interface UpdateCategoryInput {
  id: string;
  name?: string;
  description?: string;
  display_order?: number;
}

export type FileTypeFilter = 'all' | 'images' | 'pdfs' | 'documents';

export type SortOption = 'newest' | 'oldest' | 'name-asc' | 'name-desc';

export interface MaterialsFilter {
  category_id?: string | null;
  file_type?: FileTypeFilter;
  search_query?: string;
  sort_by?: SortOption;
}

// Allowed file types and their MIME types
export const ALLOWED_FILE_TYPES = {
  'image/jpeg': { extension: '.jpg', category: 'images' },
  'image/png': { extension: '.png', category: 'images' },
  'image/svg+xml': { extension: '.svg', category: 'images' },
  'application/pdf': { extension: '.pdf', category: 'pdfs' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { 
    extension: '.docx', 
    category: 'documents' 
  },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { 
    extension: '.pptx', 
    category: 'documents' 
  },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { 
    extension: '.xlsx', 
    category: 'documents' 
  },
} as const;

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

// Helper function to get file type category
export function getFileTypeCategory(mimeType: string): FileTypeFilter {
  const fileInfo = ALLOWED_FILE_TYPES[mimeType as keyof typeof ALLOWED_FILE_TYPES];
  return fileInfo?.category || 'all';
}

// Helper function to validate file type
export function isValidFileType(mimeType: string): boolean {
  return mimeType in ALLOWED_FILE_TYPES;
}

// Helper function to format file size
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

// Helper function to sanitize filename
export function sanitizeFilename(filename: string): string {
  // Remove special characters and spaces, keep only alphanumeric, dots, hyphens, underscores
  const name = filename.substring(0, filename.lastIndexOf('.')) || filename;
  const extension = filename.substring(filename.lastIndexOf('.'));
  const sanitized = name
    .replace(/[^a-zA-Z0-9.-_]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_|_$/g, '');
  return sanitized + extension;
}

// Helper function to get file icon based on MIME type
export function getFileIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType === 'application/pdf') return '📄';
  if (mimeType.includes('word')) return '📝';
  if (mimeType.includes('presentation')) return '📊';
  if (mimeType.includes('spreadsheet')) return '📈';
  return '📁';
}

// Helper function to check if file can be previewed
export function canPreviewFile(mimeType: string): boolean {
  return mimeType.startsWith('image/') || mimeType === 'application/pdf';
}

